import React from 'react';

/**
 * The five things "nothing here" can mean.
 *
 * Collapsing them into one grey box is how a brand-new tenant ends up being told
 * to clear filters they never set, and how a failed request ends up looking like
 * a quiet day. So each variant carries its own icon, wording and tone, and the
 * tone is never carried by colour alone — the icon changes with it.
 *
 *   no-data        nothing has arrived yet          → points at the integration
 *   no-results     the filters excluded everything  → offers "Clear filters"
 *   error          the request failed               → offers "Try again" + the reason
 *   not-configured the tenant has not set this up   → points at settings
 *   all-clear      genuinely nothing to do          → good tone, NOT a failure
 *
 * The `action` node is always supplied by the caller: only the screen knows
 * where "Connect a phone system" or "Clear filters" actually goes.
 */
const VARIANTS = {
  'no-data': {
    icon: 'fa-inbox',
    title: 'No calls have arrived yet',
    body: 'Calls show up here within a few minutes of your phone system being connected.',
    // Neutral chrome: ink-500 is the AA floor and the only ink allowed on an icon
    // that still has to be read as an icon.
    iconClass: 'border-line bg-surface-2 text-ink-500',
  },
  'no-results': {
    icon: 'fa-filter',
    title: 'No calls match these filters',
    body: 'There is data in this period - the filters currently exclude all of it. Widen the date range or drop a filter.',
    iconClass: 'border-line bg-surface-2 text-ink-500',
  },
  error: {
    icon: 'fa-triangle-exclamation',
    title: 'This did not load',
    // Said plainly, because the alternative users have learned to expect from
    // dashboards is a confident row of zeros.
    body: 'The request failed, so nothing is shown rather than a figure that might be wrong.',
    iconClass: 'border-bad-line bg-bad-tint text-bad-text',
  },
  'not-configured': {
    icon: 'fa-sliders',
    title: 'Not set up yet',
    body: 'This needs a one-time setup in Settings before there is anything to measure.',
    iconClass: 'border-brand-line bg-brand-tint text-brand',
  },
  'all-clear': {
    icon: 'fa-circle-check',
    title: 'All clear',
    body: 'Nothing in this period needs your attention.',
    iconClass: 'border-good-line bg-good-tint text-good-text',
  },
};

/**
 * Accept either a ready-made node or a Font Awesome name. `'fa-inbox'` is the
 * common case; a full `'fas fa-inbox'` string passes through untouched.
 */
const renderIcon = (icon) => {
  if (!icon) return null;
  if (typeof icon !== 'string') return icon;
  return <i className={icon.includes(' ') ? icon : `fas ${icon}`} aria-hidden="true" />;
};

/** An Error, a string, or nothing — never "[object Object]" on screen. */
const reasonText = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || null;
  return null;
};

/**
 * @param {object} props
 * @param {'no-data'|'no-results'|'error'|'not-configured'|'all-clear'} [props.variant]
 * @param {React.ReactNode} [props.title]  overrides the variant's headline
 * @param {React.ReactNode|Error} [props.body]
 *   For every variant except `error` this replaces the default sentence. For
 *   `error` the default sentence stays and this is shown beneath it as the
 *   machine reason (an Error or its message), because "why" and "what now" are
 *   two different pieces of information and the user needs both.
 * @param {React.ReactNode} [props.action]  rendered below the body
 * @param {React.ReactNode|string} [props.icon]  overrides the variant's icon
 * @param {boolean} [props.compact]  half the vertical padding, for in-card use
 */
export function EmptyState({
  variant = 'no-data',
  title,
  body,
  action,
  icon,
  compact = false,
  className = '',
}) {
  const isError = variant === 'error';
  const config = VARIANTS[variant] || VARIANTS['no-data'];

  const headline = title ?? config.title;
  const prose = isError ? config.body : body ?? config.body;
  const reason = isError ? reasonText(body) : null;

  return (
    <div
      // A failure that replaced content is worth interrupting for; the other four
      // are announced politely as the region they now occupy.
      role={isError ? 'alert' : 'status'}
      className={`flex flex-col items-center justify-center text-center ${
        compact ? 'px-4 py-5' : 'px-6 py-10'
      } ${className}`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-lg border ${
          config.iconClass
        } ${compact ? 'h-8 w-8 text-body' : 'h-10 w-10 text-title'}`}
      >
        {renderIcon(icon || config.icon)}
      </span>

      <p
        className={`${compact ? 'mt-2 text-body' : 'mt-3 text-title-sm'} font-semibold text-ink-900`}
      >
        {headline}
      </p>

      {prose ? (
        <p className={`mt-1 max-w-sm text-ink-500 ${compact ? 'text-label' : 'text-body'}`}>
          {prose}
        </p>
      ) : null}

      {reason ? (
        <p
          // The verbatim server reason, kept in mono so an id or status code can be
          // read back accurately, and clamped so a stack trace cannot own the page.
          title={reason}
          className="mt-3 max-w-md break-words rounded-md border border-bad-line bg-bad-tint px-3 py-2 font-mono text-micro text-bad-text line-clamp-3"
        >
          {reason}
        </p>
      ) : null}

      {action ? (
        <div
          className={`flex flex-wrap items-center justify-center gap-2 ${
            compact ? 'mt-3' : 'mt-4'
          }`}
        >
          {action}
        </div>
      ) : null}
    </div>
  );
}

export default EmptyState;
